<template>
  <div>
    <p v-if="questionsMode === true" class="text-sm text-gray-500 mb-4">
      {{ t('aiChatAgentSetup.advanced.excelQuestionAnswerDescription', 'Upload the tested-response template to fill AI responses in column 2. In Light setup, rows marked No or Partial can be added to the knowledge base as question and corrected answer pairs.') }}
    </p>
    <p v-else class="text-sm text-gray-500 mb-4">
      {{ testedResponsesMode === true ? t('aiChatAgentSetup.advanced.excelFineTuningTestedResponsesDescription', 'Upload reviewed AI answers with the human-correct answer. Test the agent or create rules that learn business context from the corrections.') : t('aiChatAgentSetup.advanced.excelFineTuningDescription', 'Upload an Excel file with the AI answer, wrong answer, and correct answer. The agent learns context, fixes, and organizational know-how from tested responses.') }}
    </p>
    <input ref="fileInputRef" type="file" class="hidden" accept=".xlsx,.xls" @change="onFileChange" />
    <div v-if="testedResponsesMode === true" dir="ltr" class="flex items-center justify-between mb-5">
      <button type="button" class="btn" :disabled="isBusy === true" @click="downloadTestedResponsesTemplate">
        <Icon icon="heroicons:arrow-down-tray" class="w-5 h-5 mr-2" />
        {{ t('aiChatAgentSetup.advanced.excelFineTuningDownloadTemplate', 'Download Excel template') }}
      </button>
      <div class="flex items-center">
        <span class="text-sm text-gray-500 mr-3">{{ selectedFileLabel }}</span>
        <button type="button" class="btn" :disabled="isBusy === true" @click="openFilePicker">
          <Icon icon="heroicons:arrow-up-tray" class="w-5 h-5 mr-2" />
          {{ t('aiChatAgentSetup.advanced.excelFineTuningUploadExcel', 'Upload Excel') }}
        </button>
      </div>
    </div>
    <div v-else class="flex flex-wrap items-center mb-4">
      <button type="button" class="btn mr-3 mb-3" :disabled="isBusy === true" @click="openFilePicker">
        <Icon icon="heroicons:document-arrow-up" class="w-5 h-5 mr-2" />
        {{ t('aiChatAgentSetup.advanced.excelFineTuningChooseFile', 'Choose Excel template') }}
      </button>
      <span class="text-sm text-gray-500 mb-3">{{ selectedFileLabel }}</span>
    </div>
    <label v-if="isLightSetup === true && questionsMode === false" class="label cursor-pointer justify-start mb-4">
      <input v-model="uploadNoAnswersAsQa" type="checkbox" class="checkbox mr-3" :disabled="isBusy === true" />
      <span class="label-text">{{ t('aiChatAgentSetup.advanced.excelFineTuningUploadNoAnswersAsQa', 'Add rows marked No or Partial in the template as question and corrected answer knowledge') }}</span>
    </label>
    <div v-if="testedResponsesMode === true" class="grid grid-cols-1 md:grid-cols-2">
      <div class="border border-base-300 bg-base-200 rounded-box p-5 mr-0 md:mr-3 mb-3 md:mb-0">
        <div class="flex items-start">
          <Icon icon="heroicons:clipboard-document-check" class="w-6 h-6 text-primary mr-3 shrink-0" />
          <div>
            <div class="font-semibold">{{ t('aiChatAgentSetup.advanced.excelFineTuningTestAnswersTitle', 'Test wrong answers against the agent') }}</div>
            <p class="text-sm text-gray-500 mt-1 mb-4">{{ t('aiChatAgentSetup.advanced.excelFineTuningTestAnswersDescription', 'Ask the agent each Excel question three times, then compare each response with the human-correct answer.') }}</p>
          </div>
        </div>
        <button type="button" class="btn" :disabled="canRun !== true" @click="runRetest">
          <span v-if="isRetestRunning === true" class="loading loading-spinner loading-sm mr-2"></span>
          {{ t('aiChatAgentSetup.advanced.excelFineTuningRetestRun', 'Test wrong answers') }}
        </button>
      </div>
      <div class="border border-base-300 bg-base-200 rounded-box p-5">
        <div class="flex items-start">
          <Icon icon="heroicons:light-bulb" class="w-6 h-6 text-primary mr-3 shrink-0" />
          <div>
            <div class="font-semibold">{{ t('aiChatAgentSetup.advanced.excelFineTuningLearnRulesTitle', 'Fine tune from human-correct answers') }}</div>
            <p class="text-sm text-gray-500 mt-1 mb-4">{{ t('aiChatAgentSetup.advanced.excelFineTuningLearnRulesDescription', 'Create reusable rules from wrong AI answers and human corrections, including business context, source behavior, and required answer details.') }}</p>
          </div>
        </div>
        <button type="button" class="btn" :disabled="canRun !== true" @click="runRules">
          <span v-if="isRulesRunning === true" class="loading loading-spinner loading-sm mr-2"></span>
          {{ t('aiChatAgentSetup.advanced.excelFineTuningRulesRun', 'Create fine-tuning rules') }}
        </button>
      </div>
    </div>
    <div v-else class="flex flex-wrap items-center">
      <button v-if="questionsMode === true" type="button" class="btn mr-3 mb-3" :disabled="canRun !== true" @click="fillAiResponses">
        <span v-if="isFillingResponses === true" class="loading loading-spinner loading-sm mr-2"></span>
        {{ t('aiChatAgentSetup.advanced.excelFineTuningFillResponses', 'Fill AI responses in column 2') }}
      </button>
      <button v-if="questionsMode === true && isLightSetup === true" type="button" class="btn mr-3 mb-3" :disabled="canRun !== true" @click="addMarkedRowsToKnowledge">
        <span v-if="isRunning === true" class="loading loading-spinner loading-sm mr-2"></span>
        {{ t('aiChatAgentSetup.advanced.excelFineTuningAddMarkedQa', 'Add template-marked wrong answers to knowledge') }}
      </button>
      <button v-if="questionsMode === false" type="button" class="btn mr-3 mb-3" :disabled="canRun !== true" @click="runFineTuning">
        <span v-if="isRunning === true" class="loading loading-spinner loading-sm mr-2"></span>
        {{ t('aiChatAgentSetup.advanced.excelFineTuningRun', 'Run Excel Fine Tuning') }}
      </button>
      <button v-if="questionsMode === false" type="button" class="btn mr-3 mb-3" :disabled="canRun !== true" @click="runRetest">
        <span v-if="isRetestRunning === true" class="loading loading-spinner loading-sm mr-2"></span>
        {{ t('aiChatAgentSetup.advanced.excelFineTuningRetestRun', 'Test Again And Color') }}
      </button>
      <button v-if="questionsMode === false" type="button" class="btn mb-3" :disabled="canRun !== true" @click="runRules">
        <span v-if="isRulesRunning === true" class="loading loading-spinner loading-sm mr-2"></span>
        {{ t('aiChatAgentSetup.advanced.excelFineTuningRulesRun', 'Create AI Rules From Failed And Partial Rows') }}
      </button>
    </div>
    <div v-if="agentId.trim().length === 0" class="mt-4 alert alert-warning text-sm">
      {{ t('aiChatAgentSetup.advanced.excelFineTuningEditOnly', 'Save the agent first, then run Excel fine tuning from edit mode.') }}
    </div>
    <div v-if="errorMessage.length > 0" class="mt-4 alert alert-error text-sm">{{ errorMessage }}</div>
    <div v-if="successMessage.length > 0" class="mt-4 alert alert-success text-sm">{{ successMessage }}</div>
    <div v-if="answerFillSummary !== null" class="mt-4 p-4 bg-base-100 border border-base-300 rounded-box text-sm">
      <div>{{ t('aiChatAgentSetup.advanced.excelFineTuningAnswerRows', 'Questions processed') }}: {{ answerFillSummary.processedRows }}</div>
      <div>{{ t('aiChatAgentSetup.advanced.excelFineTuningAnswersFilled', 'AI responses written to column 2') }}: {{ answerFillSummary.filledResponses }}</div>
    </div>
    <div v-if="summary !== null" class="mt-4 p-4 bg-base-100 border border-base-300 rounded-box text-sm">
      <div class="font-medium mb-2">{{ t('aiChatAgentSetup.advanced.excelFineTuningSummary', 'Last run summary') }}</div>
      <div>{{ t('aiChatAgentSetup.advanced.excelFineTuningProcessed', 'Processed rows') }}: {{ summary.processedRows }}</div>
      <div>{{ t('aiChatAgentSetup.advanced.excelFineTuningGreen', 'Green rows') }}: {{ summary.greenRows }}</div>
      <div>{{ t('aiChatAgentSetup.advanced.excelFineTuningRed', 'Red rows') }}: {{ summary.redRows }}</div>
      <div v-if="summary.qaUploadEnabled === true">{{ t('aiChatAgentSetup.advanced.excelFineTuningUploadedQaPairs', 'Uploaded Q&A pairs') }}: {{ summary.uploadedQaPairs }} / {{ summary.qaPairsForUpload }}</div>
      <div v-if="qaUploadError.length > 0" class="text-error">{{ qaUploadError }}</div>
    </div>
    <ExcelFineTuningRetestModal
      v-if="retestJob !== null || isRetestRunning === true"
      :visible="true"
      :inline="true"
      :job="retestJob"
      :is-running="isRetestRunning === true"
      :is-cancelling="isRetestCancelling === true"
      :error="errorMessage"
      @download="downloadRetestWorkbook"
      @cancel="cancelRetestJob"
    />
    <ExcelFineTuningRulesModal
      v-if="rulesJob !== null || isRulesRunning === true || rulesError.length > 0"
      :visible="true"
      :inline="true"
      :job="rulesJob"
      :is-running="isRulesRunning === true"
      :error="rulesError"
      @approve-rules="approveGeneratedRules"
      @export-missing-knowledge="exportMissingKnowledge"
    />
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { Icon } from '@iconify/vue/dist/offline';
import { globalState } from '@/globalState.js';
import { useI18n } from '@/i18n';
import ExcelFineTuningRetestModal from './ExcelFineTuningRetestModal.vue';
import ExcelFineTuningRulesModal from './ExcelFineTuningRulesModal.vue';

const props = defineProps({
  agentId: { type: String, default: '' },
  setupType: { type: String, default: 'full' },
  questionsMode: { type: Boolean, default: false },
  testedResponsesMode: { type: Boolean, default: false }
});

const emit = defineEmits(['approve-generated-rules', 'learn-question-answers']);
const { t } = useI18n();
const fileInputRef = ref(null);
const selectedFile = ref(null);
const selectedFileName = ref('');
const isRunning = ref(false);
const isFillingResponses = ref(false);
const isRetestRunning = ref(false);
const isRetestCancelling = ref(false);
const isRulesRunning = ref(false);
const errorMessage = ref('');
const successMessage = ref('');
const rulesError = ref('');
const summary = ref(null);
const answerFillSummary = ref(null);
const uploadNoAnswersAsQa = ref(true);
const retestJob = ref(null);
const rulesJob = ref(null);
const downloadedRetestJobId = ref('');
const retestSocket = ref(null);
let retestSocketJobId = '';
let retestPollTimer = null;
let rulesPollTimer = null;
let workbookResolve = null;
let workbookTimer = null;
let workbookPromise = null;
const retestJobStoragePrefix = 'excelFineTuningRetestJob:';

const agentId = computed(() => typeof props.agentId === 'string' ? props.agentId : '');
const isLightSetup = computed(() => props.setupType === 'light');
const questionsMode = computed(() => props.questionsMode === true);
const testedResponsesMode = computed(() => props.testedResponsesMode === true);
const selectedFileLabel = computed(() => selectedFileName.value.length > 0 ? selectedFileName.value : t('aiChatAgentSetup.advanced.excelFineTuningNoFile', 'No file selected'));
const isBusy = computed(() => isRunning.value === true || isFillingResponses.value === true || isRetestRunning.value === true || isRulesRunning.value === true);
const canRun = computed(() => agentId.value.trim().length > 0 && selectedFile.value !== null && isBusy.value !== true);
const qaUploadError = computed(() => {
  if (summary.value === null) {
    return '';
  }
  return typeof summary.value.qaUploadError === 'string' ? summary.value.qaUploadError : '';
});

function getRetestJobStorageKey() {
  const id = agentId.value.trim();
  if (id.length === 0) {
    return '';
  }
  return `${retestJobStoragePrefix}${id}`;
}

function saveRetestJobId(jobId) {
  const storageKey = getRetestJobStorageKey();
  if (storageKey.length === 0 || typeof window === 'undefined') {
    return;
  }
  try {
    if (typeof jobId === 'string' && jobId.length > 0) {
      window.localStorage.setItem(storageKey, jobId);
    } else {
      window.localStorage.removeItem(storageKey);
    }
  } catch (error) {}
}

function getSavedRetestJobId() {
  const storageKey = getRetestJobStorageKey();
  if (storageKey.length === 0 || typeof window === 'undefined') {
    return '';
  }
  try {
    const jobId = window.localStorage.getItem(storageKey);
    return typeof jobId === 'string' ? jobId : '';
  } catch (error) {
    return '';
  }
}

function getAuthToken() {
  if (globalState.authUsesHttpOnlyCookies === true || typeof document === 'undefined') {
    return '';
  }
  const rows = document.cookie.split('; ');
  for (let index = 0; index < rows.length; index = index + 1) {
    if (rows[index].startsWith('authToken=') === true) {
      const parts = rows[index].split('=');
      return parts.length > 1 ? parts[1] : '';
    }
  }
  return '';
}

function getAxiosConfig(extraConfig) {
  const config = extraConfig !== undefined && extraConfig !== null && typeof extraConfig === 'object' ? { ...extraConfig } : {};
  const token = getAuthToken();
  config.headers = { 'Content-Type': 'application/json' };
  if (token.length > 0) {
    config.headers.Authorization = `Bearer ${token}`;
  } else {
    config.withCredentials = true;
  }
  return config;
}

function encodeSegment(value) {
  return encodeURIComponent(value !== undefined && value !== null ? String(value) : '');
}

function openFilePicker() {
  errorMessage.value = '';
  successMessage.value = '';
  if (fileInputRef.value !== null) {
    fileInputRef.value.click();
  }
}

function onFileChange(event) {
  errorMessage.value = '';
  successMessage.value = '';
  summary.value = null;
  answerFillSummary.value = null;
  const target = event.target;
  if (target === null || target === undefined || target.files === null || target.files === undefined || target.files.length === 0) {
    selectedFile.value = null;
    selectedFileName.value = '';
    return;
  }
  const file = target.files[0];
  const fileName = typeof file.name === 'string' ? file.name : '';
  const lowerName = fileName.toLowerCase();
  if (lowerName.endsWith('.xlsx') !== true && lowerName.endsWith('.xls') !== true) {
    selectedFile.value = null;
    selectedFileName.value = '';
    errorMessage.value = t('aiChatAgentSetup.advanced.excelFineTuningInvalidFile', 'Please choose an Excel file.');
    target.value = '';
    return;
  }
  selectedFile.value = file;
  selectedFileName.value = fileName;
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Failed to read Excel file.'));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read Excel file.'));
    reader.readAsDataURL(file);
  });
}

function readFileAsArrayBuffer(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (reader.result instanceof ArrayBuffer) {
        resolve(reader.result);
      } else {
        reject(new Error('Failed to read Excel file.'));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read Excel file.'));
    reader.readAsArrayBuffer(file);
  });
}

function getTemplateCellText(value) {
  if (value === undefined) {
    return '';
  }
  if (value === null) {
    return '';
  }
  return String(value).trim();
}

function normalizeTemplateHeader(value) {
  return getTemplateCellText(value).toLowerCase().replace(/[\s_\-–—?:]+/g, '');
}

function findTemplateHeaderRow(rows) {
  const maxIndex = rows.length > 10 ? 10 : rows.length;
  for (let rowIndex = 0; rowIndex < maxIndex; rowIndex = rowIndex + 1) {
    const row = Array.isArray(rows[rowIndex]) === true ? rows[rowIndex] : [];
    let populatedCells = 0;
    for (let cellIndex = 0; cellIndex < row.length; cellIndex = cellIndex + 1) {
      if (getTemplateCellText(row[cellIndex]).length > 0) {
        populatedCells = populatedCells + 1;
      }
    }
    if (populatedCells >= 2) {
      return rowIndex;
    }
  }
  return -1;
}

function findTemplateColumn(headers, aliases) {
  for (let headerIndex = 0; headerIndex < headers.length; headerIndex = headerIndex + 1) {
    const header = normalizeTemplateHeader(headers[headerIndex]);
    for (let aliasIndex = 0; aliasIndex < aliases.length; aliasIndex = aliasIndex + 1) {
      if (header === normalizeTemplateHeader(aliases[aliasIndex])) {
        return headerIndex;
      }
    }
  }
  return -1;
}

function isMarkedWrongAnswer(value) {
  const status = getTemplateCellText(value).toLowerCase();
  const markedStatuses = ['no', 'partial', 'partially', 'yes and no', 'yes/no', 'לא', 'חלקית', 'חלקי', 'כן ולא', 'כן/לא'];
  return markedStatuses.includes(status);
}

async function addMarkedRowsToKnowledge() {
  errorMessage.value = '';
  successMessage.value = '';
  summary.value = null;
  answerFillSummary.value = null;
  if (validateRun() !== true) {
    return;
  }
  isRunning.value = true;
  try {
    const workbook = XLSX.read(new Uint8Array(await readFileAsArrayBuffer(selectedFile.value)), { type: 'array' });
    const firstSheetName = workbook.SheetNames[0];
    const worksheet = firstSheetName !== undefined ? workbook.Sheets[firstSheetName] : undefined;
    const rows = worksheet !== undefined ? XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' }) : [];
    const headerRowIndex = findTemplateHeaderRow(rows);
    if (headerRowIndex < 0) {
      throw new Error(t('aiChatAgentSetup.advanced.excelFineTuningTemplateInvalid', 'The Excel template headers could not be found.'));
    }
    const headers = Array.isArray(rows[headerRowIndex]) === true ? rows[headerRowIndex] : [];
    const questionColumn = findTemplateColumn(headers, ['Question', 'User Question', 'Query', 'שאלה', 'שאלת משתמש', 'שאלת המשתמש', 'הודעת הפונה לAI', 'السؤال']);
    const correctAnswerColumn = findTemplateColumn(headers, ['Correct Full Answer', 'Correct Answer', 'Expected Answer', 'אם התשובה לא הייתה תקינה מה התשובה המלאה התקינה צריכה להיות', 'אם התשובה לא היתה תקינה מה התשובה המלאה התקינה צריכה להיות', 'מה התשובה המלאה התקינה צריכה להיות', 'התשובה המלאה התקינה', 'תשובה מלאה תקינה']);
    const statusColumn = findTemplateColumn(headers, ['Response OK', 'Answer OK', 'Status', 'האם היא הייתה תקינה או לא', 'האם היא היתה תקינה או לא', 'האם התשובה תקינה או לא', 'האם התשובה הייתה תקינה או לא', 'האם התשובה היתה תקינה או לא', 'תקין', 'תקינות', 'סטטוס']);
    if (questionColumn < 0) {
      throw new Error(t('aiChatAgentSetup.advanced.excelFineTuningTemplateInvalid', 'The template must include question, status, and correct answer columns.'));
    }
    if (correctAnswerColumn < 0) {
      throw new Error(t('aiChatAgentSetup.advanced.excelFineTuningTemplateInvalid', 'The template must include question, status, and correct answer columns.'));
    }
    if (statusColumn < 0) {
      throw new Error(t('aiChatAgentSetup.advanced.excelFineTuningTemplateInvalid', 'The template must include question, status, and correct answer columns.'));
    }
    const entries = [];
    for (let rowIndex = headerRowIndex + 1; rowIndex < rows.length; rowIndex = rowIndex + 1) {
      const row = Array.isArray(rows[rowIndex]) === true ? rows[rowIndex] : [];
      if (isMarkedWrongAnswer(row[statusColumn]) !== true) {
        continue;
      }
      const question = getTemplateCellText(row[questionColumn]);
      const answer = getTemplateCellText(row[correctAnswerColumn]);
      if (question.length > 0 && answer.length > 0) {
        entries.push({
          originalQuery: question,
          originalResponse: '',
          fineTunedAnswer: answer,
          hiddenContext: '',
          companyKnowHow: '',
          forceExactAnswer: false,
          suppressToast: true
        });
      }
    }
    if (entries.length === 0) {
      errorMessage.value = t('aiChatAgentSetup.advanced.excelFineTuningNoMarkedRows', 'No valid rows marked No or Partial were found.');
      return;
    }
    emit('learn-question-answers', entries);
    successMessage.value = t('aiChatAgentSetup.advanced.excelFineTuningQaReady', '{count} knowledge entries were added. Save the agent to publish them.').replace('{count}', String(entries.length));
  } catch (error) {
    errorMessage.value = error instanceof Error && error.message.length > 0 ? error.message : await getErrorMessage(error);
  } finally {
    isRunning.value = false;
  }
}

function downloadWorkbook(blob, fileName) {
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(objectUrl);
}

function downloadTestedResponsesTemplate() {
  const workbook = XLSX.utils.book_new();
  const rows = [
    ['הודעת הפונה לAI', 'תשובת הAI', 'האם היא הייתה תקינה או לא', 'אם התשובה לא הייתה תקינה - מה התשובה המלאה התקינה צריכה להיות'],
    ['', '', '', '']
  ];
  const sheet = XLSX.utils.aoa_to_sheet(rows);
  sheet['!cols'] = [{ wch: 42 }, { wch: 42 }, { wch: 28 }, { wch: 55 }];
  XLSX.utils.book_append_sheet(workbook, sheet, 'Tested responses');
  const output = XLSX.write(workbook, { type: 'array', bookType: 'xlsx' });
  downloadWorkbook(new Blob([output], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), 'tested-responses-fine-tuning-template.xlsx');
}

function parseSummary(headers) {
  if (headers === undefined || headers === null || typeof headers['x-excel-fine-tuning-summary'] !== 'string') {
    return null;
  }
  try {
    return JSON.parse(decodeURIComponent(headers['x-excel-fine-tuning-summary']));
  } catch (error) {
    return null;
  }
}

async function getErrorMessage(error) {
  let fallback = t('aiChatAgentSetup.advanced.excelFineTuningFailed', 'Excel fine tuning failed.');
  if (error !== undefined && error !== null && error.response !== undefined && error.response !== null) {
    const data = error.response.data;
    if (data instanceof Blob) {
      try {
        const parsed = JSON.parse(await data.text());
        if (typeof parsed.error === 'string' && parsed.error.length > 0) {
          fallback = parsed.error;
        }
      } catch (blobError) {
        fallback = fallback;
      }
    } else if (data !== undefined && data !== null && typeof data.error === 'string' && data.error.length > 0) {
      fallback = data.error;
    }
  } else if (error !== undefined && error !== null && typeof error.message === 'string' && error.message.length > 0) {
    fallback = error.message;
  }
  return fallback;
}

function validateRun() {
  if (agentId.value.trim().length === 0) {
    errorMessage.value = t('aiChatAgentSetup.advanced.excelFineTuningEditOnly', 'Save the agent first, then run Excel fine tuning from edit mode.');
    return false;
  }
  if (selectedFile.value === null) {
    errorMessage.value = t('aiChatAgentSetup.advanced.excelFineTuningChooseRequired', 'Choose an Excel template first.');
    return false;
  }
  return true;
}

async function runFineTuning() {
  errorMessage.value = '';
  successMessage.value = '';
  summary.value = null;
  answerFillSummary.value = null;
  if (validateRun() !== true) {
    return;
  }
  isRunning.value = true;
  try {
    const fileDataUrl = await readFileAsDataUrl(selectedFile.value);
    const response = await axios.post(
      `${globalState.apiEndpoint}/api/agents/${encodeSegment(agentId.value.trim())}/excelFineTuning`,
      { fileName: selectedFileName.value, fileBase64: fileDataUrl, uploadNoAnswersAsQa: isLightSetup.value === true && uploadNoAnswersAsQa.value === true },
      getAxiosConfig({ responseType: 'blob', timeout: 0 })
    );
    const outputName = selectedFileName.value.toLowerCase().endsWith('.xlsx') === true ? `${selectedFileName.value.slice(0, -5)}-fine-tuning-checked.xlsx` : 'excel-fine-tuning-checked.xlsx';
    downloadWorkbook(new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), outputName);
    summary.value = parseSummary(response.headers);
    successMessage.value = t('aiChatAgentSetup.advanced.excelFineTuningDone', 'Excel fine tuning finished and the colored workbook was downloaded.');
  } catch (error) {
    errorMessage.value = await getErrorMessage(error);
  } finally {
    isRunning.value = false;
  }
}

async function fillAiResponses() {
  errorMessage.value = '';
  successMessage.value = '';
  summary.value = null;
  answerFillSummary.value = null;
  if (validateRun() !== true) {
    return;
  }
  isFillingResponses.value = true;
  try {
    const fileDataUrl = await readFileAsDataUrl(selectedFile.value);
    const response = await axios.post(
      `${globalState.apiEndpoint}/api/agents/${encodeSegment(agentId.value.trim())}/excelFineTuning`,
      { fileName: selectedFileName.value, fileBase64: fileDataUrl, operation: 'fillAiResponses' },
      getAxiosConfig({ responseType: 'blob', timeout: 0 })
    );
    const outputName = selectedFileName.value.toLowerCase().endsWith('.xlsx') === true ? `${selectedFileName.value.slice(0, -5)}-with-ai-responses.xlsx` : 'excel-fine-tuning-with-ai-responses.xlsx';
    downloadWorkbook(new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), outputName);
    answerFillSummary.value = parseSummary(response.headers);
    successMessage.value = t('aiChatAgentSetup.advanced.excelFineTuningFillResponsesDone', 'AI responses were written only to column 2 and the completed workbook was downloaded.');
  } catch (error) {
    errorMessage.value = await getErrorMessage(error);
  } finally {
    isFillingResponses.value = false;
  }
}

function clearRetestPollTimer() {
  if (retestPollTimer !== null) {
    window.clearTimeout(retestPollTimer);
    retestPollTimer = null;
  }
}

function clearRulesPollTimer() {
  if (rulesPollTimer !== null) {
    window.clearTimeout(rulesPollTimer);
    rulesPollTimer = null;
  }
}

function settleWorkbookRequest(result) {
  if (workbookTimer !== null) {
    window.clearTimeout(workbookTimer);
    workbookTimer = null;
  }
  const resolver = workbookResolve;
  workbookResolve = null;
  workbookPromise = null;
  if (typeof resolver === 'function') {
    resolver(result === true);
  }
}

function closeRetestSocket() {
  const socket = retestSocket.value;
  retestSocket.value = null;
  retestSocketJobId = '';
  settleWorkbookRequest(false);
  if (socket !== null && socket !== undefined) {
    socket.onopen = null;
    socket.onmessage = null;
    socket.onerror = null;
    socket.onclose = null;
    if (socket.readyState === 0 || socket.readyState === 1) {
      socket.close();
    }
  }
}

function getSocketUrl() {
  const endpoint = typeof globalState.apiEndpoint === 'string' ? globalState.apiEndpoint.trim() : '';
  if (endpoint.length === 0) {
    return '';
  }
  const cleanEndpoint = endpoint.endsWith('/') === true ? endpoint.slice(0, -1) : endpoint;
  const socketBase = cleanEndpoint.startsWith('https://') === true ? `wss://${cleanEndpoint.slice(8)}` : cleanEndpoint.startsWith('http://') === true ? `ws://${cleanEndpoint.slice(7)}` : cleanEndpoint;
  return `${socketBase}/api/agents/excelFineTuning/retest/ws`;
}

function sendSocketPayload(payload) {
  const socket = retestSocket.value;
  if (socket === null || socket === undefined || socket.readyState !== 1) {
    return false;
  }
  try {
    socket.send(JSON.stringify(payload));
    return true;
  } catch (error) {
    return false;
  }
}

function decodeWorkbook(base64Value) {
  const binary = window.atob(base64Value);
  const chunks = [];
  for (let offset = 0; offset < binary.length; offset = offset + 8192) {
    const slice = binary.slice(offset, offset + 8192);
    const numbers = new Array(slice.length);
    for (let index = 0; index < slice.length; index = index + 1) {
      numbers[index] = slice.charCodeAt(index);
    }
    chunks.push(new Uint8Array(numbers));
  }
  return new Blob(chunks, { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
}

function handleWorkbookPayload(payload) {
  const workbookBase64 = payload !== undefined && payload !== null && typeof payload.workbookBase64 === 'string' ? payload.workbookBase64 : '';
  if (workbookBase64.length === 0) {
    settleWorkbookRequest(false);
    return;
  }
  const jobId = typeof payload.jobId === 'string' ? payload.jobId : '';
  const fileName = typeof payload.fileName === 'string' && payload.fileName.length > 0 ? payload.fileName : 'excel-fine-tuning-retested.xlsx';
  downloadWorkbook(decodeWorkbook(workbookBase64), fileName);
  if (payload.summary !== undefined) {
    summary.value = payload.summary;
  }
  downloadedRetestJobId.value = jobId;
  settleWorkbookRequest(true);
  closeRetestSocket();
}

async function handleRetestJob(job) {
  if (job === undefined || job === null || typeof job !== 'object') {
    return;
  }
  retestJob.value = job;
  const jobId = typeof job.jobId === 'string' ? job.jobId : '';
  if (job.status === 'queued' || job.status === 'running' || job.status === 'cancelling') {
    isRetestRunning.value = true;
    isRetestCancelling.value = job.status === 'cancelling';
    saveRetestJobId(jobId);
  } else if (job.status === 'completed') {
    isRetestRunning.value = false;
    isRetestCancelling.value = false;
    summary.value = job.summary !== undefined ? job.summary : null;
    saveRetestJobId('');
    clearRetestPollTimer();
    if (jobId.length > 0 && downloadedRetestJobId.value !== jobId) {
      await downloadRetestWorkbook();
    }
    if (errorMessage.value.length === 0) {
      successMessage.value = t('aiChatAgentSetup.advanced.excelFineTuningRetestDone', 'Excel retest finished and the colored workbook was downloaded.');
    }
  } else if (job.status === 'failed') {
    isRetestRunning.value = false;
    isRetestCancelling.value = false;
    saveRetestJobId('');
    errorMessage.value = typeof job.error === 'string' && job.error.length > 0 ? job.error : t('aiChatAgentSetup.advanced.excelFineTuningRetestFailed', 'Excel retest failed.');
    clearRetestPollTimer();
    closeRetestSocket();
  } else if (job.status === 'cancelled') {
    isRetestRunning.value = false;
    isRetestCancelling.value = false;
    saveRetestJobId('');
    errorMessage.value = '';
    successMessage.value = t('aiChatAgentSetup.advanced.excelFineTuningRetestCancelled', 'Excel retest cancelled.');
    clearRetestPollTimer();
    closeRetestSocket();
  }
}

async function handleSocketMessage(event) {
  let payload = null;
  try {
    payload = JSON.parse(String(event.data));
  } catch (error) {
    payload = null;
  }
  if (payload === null || typeof payload !== 'object') {
    return;
  }
  if (payload.type === 'job') {
    await handleRetestJob(payload.job);
  } else if (payload.type === 'workbook') {
    handleWorkbookPayload(payload);
  } else if (payload.type === 'workbookNotReady') {
    settleWorkbookRequest(false);
  } else if (payload.type === 'error' && isRetestRunning.value === true) {
    errorMessage.value = typeof payload.message === 'string' ? payload.message : t('aiChatAgentSetup.advanced.excelFineTuningRetestFailed', 'Excel retest failed.');
    isRetestRunning.value = false;
    clearRetestPollTimer();
  }
}

function scheduleRetestPoll(jobId) {
  clearRetestPollTimer();
  retestPollTimer = window.setTimeout(() => pollRetestJob(jobId), 1200);
}

function connectRetestSocket(jobId) {
  closeRetestSocket();
  const socketUrl = getSocketUrl();
  if (socketUrl.length === 0 || typeof WebSocket === 'undefined') {
    return false;
  }
  try {
    const socket = new WebSocket(socketUrl);
    retestSocket.value = socket;
    retestSocketJobId = jobId;
    socket.onopen = () => sendSocketPayload({ type: 'subscribe', agentId: agentId.value.trim(), jobId });
    socket.onmessage = handleSocketMessage;
    socket.onerror = () => {};
    socket.onclose = () => {
      const closedJobId = retestSocketJobId;
      retestSocket.value = null;
      retestSocketJobId = '';
      settleWorkbookRequest(false);
      if (isRetestRunning.value === true && closedJobId.length > 0) {
        scheduleRetestPoll(closedJobId);
      }
    };
    return true;
  } catch (error) {
    return false;
  }
}

function requestWorkbookOverSocket(jobId) {
  if (workbookPromise !== null) {
    return workbookPromise;
  }
  if (sendSocketPayload({ type: 'requestWorkbook', agentId: agentId.value.trim(), jobId }) !== true) {
    return Promise.resolve(false);
  }
  workbookPromise = new Promise((resolve) => {
    workbookResolve = resolve;
    workbookTimer = window.setTimeout(() => settleWorkbookRequest(false), 15000);
  });
  return workbookPromise;
}

async function downloadRetestWorkbook(preferSocket) {
  const jobId = retestJob.value !== null && typeof retestJob.value.jobId === 'string' ? retestJob.value.jobId : '';
  if (jobId.length === 0) {
    errorMessage.value = t('aiChatAgentSetup.advanced.excelFineTuningRetestMissingJob', 'Missing retest job.');
    return;
  }
  if (preferSocket !== false && await requestWorkbookOverSocket(jobId) === true) {
    return;
  }
  try {
    const response = await axios.get(
      `${globalState.apiEndpoint}/api/agents/${encodeSegment(agentId.value.trim())}/excelFineTuning/retest/jobs/${encodeSegment(jobId)}/workbook`,
      getAxiosConfig({ responseType: 'blob', timeout: 0 })
    );
    const outputName = typeof retestJob.value.outputFileName === 'string' && retestJob.value.outputFileName.length > 0 ? retestJob.value.outputFileName : 'excel-fine-tuning-retested.xlsx';
    downloadWorkbook(new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), outputName);
    const parsed = parseSummary(response.headers);
    summary.value = parsed !== null ? parsed : retestJob.value.summary !== undefined ? retestJob.value.summary : null;
    downloadedRetestJobId.value = jobId;
    closeRetestSocket();
  } catch (error) {
    errorMessage.value = await getErrorMessage(error);
  }
}

async function pollRetestJob(jobId) {
  try {
    const response = await axios.get(
      `${globalState.apiEndpoint}/api/agents/${encodeSegment(agentId.value.trim())}/excelFineTuning/retest/jobs/${encodeSegment(jobId)}`,
      getAxiosConfig({ timeout: 0 })
    );
    if (response !== undefined && response !== null && response.data !== undefined && response.data !== null) {
      await handleRetestJob(response.data);
      if (response.data.status === 'completed' || response.data.status === 'failed') {
        return;
      }
    }
    scheduleRetestPoll(jobId);
  } catch (error) {
    isRetestRunning.value = false;
    errorMessage.value = await getErrorMessage(error);
    clearRetestPollTimer();
  }
}

async function cancelRetestJob() {
  const jobId = retestJob.value !== null && typeof retestJob.value.jobId === 'string' ? retestJob.value.jobId : '';
  if (jobId.length === 0) {
    errorMessage.value = t('aiChatAgentSetup.advanced.excelFineTuningRetestMissingJob', 'Missing retest job.');
    return;
  }
  isRetestCancelling.value = true;
  try {
    const response = await axios.post(
      `${globalState.apiEndpoint}/api/agents/${encodeSegment(agentId.value.trim())}/excelFineTuning/retest/jobs/${encodeSegment(jobId)}/cancel`,
      {},
      getAxiosConfig({ timeout: 0 })
    );
    if (response !== undefined && response !== null && response.data !== undefined && response.data !== null) {
      await handleRetestJob(response.data);
    }
  } catch (error) {
    isRetestCancelling.value = false;
    errorMessage.value = await getErrorMessage(error);
  }
}

async function restoreRetestJob() {
  const jobId = getSavedRetestJobId();
  if (jobId.length === 0 || agentId.value.trim().length === 0) {
    return;
  }
  try {
    const response = await axios.get(
      `${globalState.apiEndpoint}/api/agents/${encodeSegment(agentId.value.trim())}/excelFineTuning/retest/jobs/${encodeSegment(jobId)}`,
      getAxiosConfig({ timeout: 0 })
    );
    if (response === undefined || response === null || response.data === undefined || response.data === null) {
      return;
    }
    await handleRetestJob(response.data);
    const status = typeof response.data.status === 'string' ? response.data.status : '';
    if (status === 'queued' || status === 'running' || status === 'cancelling') {
      connectRetestSocket(jobId);
      scheduleRetestPoll(jobId);
    }
  } catch (error) {
    if (error?.response?.status === 404) {
      saveRetestJobId('');
    }
  }
}

async function runRetest() {
  errorMessage.value = '';
  successMessage.value = '';
  summary.value = null;
  answerFillSummary.value = null;
  retestJob.value = null;
  downloadedRetestJobId.value = '';
  isRetestCancelling.value = false;
  clearRetestPollTimer();
  closeRetestSocket();
  if (validateRun() !== true) {
    return;
  }
  isRetestRunning.value = true;
  try {
    const response = await axios.post(
      `${globalState.apiEndpoint}/api/agents/${encodeSegment(agentId.value.trim())}/excelFineTuning/retest/jobs`,
      { fileName: selectedFileName.value, fileBase64: await readFileAsDataUrl(selectedFile.value) },
      getAxiosConfig({ timeout: 0 })
    );
    if (response !== undefined && response !== null && response.data !== undefined && response.data !== null) {
      await handleRetestJob(response.data);
      const jobId = typeof response.data.jobId === 'string' ? response.data.jobId : '';
      if (jobId.length > 0) {
        connectRetestSocket(jobId);
        scheduleRetestPoll(jobId);
      }
    }
  } catch (error) {
    isRetestRunning.value = false;
    errorMessage.value = await getErrorMessage(error);
  }
}

function scheduleRulesPoll(jobId) {
  clearRulesPollTimer();
  rulesPollTimer = window.setTimeout(() => pollRulesJob(jobId), 1200);
}

async function pollRulesJob(jobId) {
  try {
    const response = await axios.get(
      `${globalState.apiEndpoint}/api/agents/${encodeSegment(agentId.value.trim())}/excelFineTuning/rules/${encodeSegment(jobId)}`,
      getAxiosConfig({ timeout: 0 })
    );
    if (response !== undefined && response !== null && response.data !== undefined && response.data !== null) {
      rulesJob.value = response.data;
      if (response.data.status === 'completed') {
        isRulesRunning.value = false;
        successMessage.value = t('aiChatAgentSetup.advanced.excelFineTuningRulesDone', 'AI rules are ready for inline review and approval.');
        clearRulesPollTimer();
        return;
      }
      if (response.data.status === 'failed') {
        isRulesRunning.value = false;
        rulesError.value = typeof response.data.error === 'string' ? response.data.error : t('aiChatAgentSetup.advanced.excelFineTuningRulesFailed', 'AI rule generation failed.');
        clearRulesPollTimer();
        return;
      }
    }
    scheduleRulesPoll(jobId);
  } catch (error) {
    isRulesRunning.value = false;
    rulesError.value = await getErrorMessage(error);
    clearRulesPollTimer();
  }
}

async function runRules() {
  errorMessage.value = '';
  successMessage.value = '';
  rulesError.value = '';
  rulesJob.value = null;
  answerFillSummary.value = null;
  if (validateRun() !== true) {
    rulesError.value = errorMessage.value;
    return;
  }
  isRulesRunning.value = true;
  try {
    const response = await axios.post(
      `${globalState.apiEndpoint}/api/agents/${encodeSegment(agentId.value.trim())}/excelFineTuning/rules`,
      { fileName: selectedFileName.value, fileBase64: await readFileAsDataUrl(selectedFile.value) },
      getAxiosConfig({ timeout: 0 })
    );
    if (response !== undefined && response !== null && response.data !== undefined && response.data !== null && typeof response.data.jobId === 'string') {
      rulesJob.value = response.data;
      scheduleRulesPoll(response.data.jobId);
    }
  } catch (error) {
    isRulesRunning.value = false;
    rulesError.value = await getErrorMessage(error);
  }
}

function approveGeneratedRules(rules) {
  const source = Array.isArray(rules) === true ? rules : [];
  if (source.length > 0) {
    emit('approve-generated-rules', source);
  }
}

async function exportMissingKnowledge() {
  rulesError.value = '';
  const jobId = rulesJob.value !== null && typeof rulesJob.value.jobId === 'string' ? rulesJob.value.jobId : '';
  if (jobId.length === 0) {
    rulesError.value = t('aiChatAgentSetup.advanced.excelFineTuningRulesMissingJob', 'Missing rule generation job.');
    return;
  }
  try {
    const response = await axios.get(
      `${globalState.apiEndpoint}/api/agents/${encodeSegment(agentId.value.trim())}/excelFineTuning/rules/${encodeSegment(jobId)}/missingKnowledge`,
      getAxiosConfig({ responseType: 'blob', timeout: 0 })
    );
    downloadWorkbook(new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), 'excel-fine-tuning-missing-knowledge.xlsx');
  } catch (error) {
    rulesError.value = await getErrorMessage(error);
  }
}

onMounted(() => {
  restoreRetestJob();
});

onBeforeUnmount(() => {
  clearRetestPollTimer();
  clearRulesPollTimer();
  closeRetestSocket();
});
</script>
